import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { STATE_AID_CERTIFICATE_CONFIG } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/stateAidReceived/constants.ts';
import { getDefaultStateAidFormValues } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/stateAidReceived/defaults.ts';
import { mapStateAidDtoToFormValues } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/stateAidReceived/mapDtoToFormValues.ts';
import {
  StateAidCertificateType,
  type TStateAidFormValues,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/stateAidReceived/types.ts';
import type { TDocumentGenerationFormProps } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/types.ts';
import { collectHistoricalPrefilledFieldNames } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/historicalPrefill/utils.ts';
import { getStateAidQuarterWindow } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/stateAidReceived/quarterUtils.ts';

const HISTORICAL_READ_ONLY_FIELDS = ['companyFullName', 'companyIdentifier'];

type TUseStateAidReceivedFormParams = Pick<
  TDocumentGenerationFormProps,
  | 'stateAidCertificateType'
  | 'stateAidYearPeriod'
  | 'stateAidChildPeriod'
  | 'onLoadStateAidReceived'
  | 'onSaveStateAidReceived'
  | 'historicalBody'
  | 'isHistoricalSource'
>;

export const useStateAidReceivedForm = ({
  stateAidCertificateType,
  stateAidYearPeriod,
  stateAidChildPeriod,
  onLoadStateAidReceived,
  onSaveStateAidReceived,
  historicalBody,
  isHistoricalSource,
}: TUseStateAidReceivedFormParams) => {
  const hasLoadedRef = useRef(false);
  const versionRef = useRef<number | null>(null);
  const [isLoading, setIsLoading] = useState(Boolean(onLoadStateAidReceived));
  const [historicalFields, setHistoricalFields] = useState<string[]>([]);
  const certificateType = stateAidCertificateType ?? StateAidCertificateType.Credit;
  const config = STATE_AID_CERTIFICATE_CONFIG[certificateType];
  const quarterWindow = getStateAidQuarterWindow(stateAidYearPeriod, stateAidChildPeriod);
  const form = useForm<TStateAidFormValues>({
    defaultValues: getDefaultStateAidFormValues(stateAidYearPeriod, stateAidChildPeriod),
  });

  useEffect(() => {
    if (!onLoadStateAidReceived || hasLoadedRef.current) return;

    hasLoadedRef.current = true;

    void onLoadStateAidReceived(certificateType).then((response) => {
      if (!response) {
        setIsLoading(false);
        return;
      }

      versionRef.current = response.version;
      const certificate =
        historicalBody && typeof historicalBody === 'object' ? historicalBody : response.body;
      const values = mapStateAidDtoToFormValues(
        certificate as typeof response.body,
        response,
        stateAidYearPeriod ?? '',
        stateAidChildPeriod ?? ''
      );

      form.reset(values);
      setHistoricalFields(
        isHistoricalSource
          ? collectHistoricalPrefilledFieldNames(values, HISTORICAL_READ_ONLY_FIELDS)
          : []
      );
      setIsLoading(false);
    });
  }, [
    certificateType,
    form,
    historicalBody,
    isHistoricalSource,
    onLoadStateAidReceived,
    stateAidChildPeriod,
    stateAidYearPeriod,
  ]);

  const handleSubmit = async (values: TStateAidFormValues) => {
    if (!onSaveStateAidReceived || versionRef.current === null) return;

    const result = await onSaveStateAidReceived(certificateType, values, versionRef.current);
    if (result !== 'success') return;

    form.reset(values);
    void onLoadStateAidReceived?.(certificateType).then((response) => {
      if (response) versionRef.current = response.version;
    });
  };

  return {
    config,
    form,
    historicalFields,
    isLoading,
    quarterWindow: quarterWindow ?? [],
    handleSubmit,
  };
};
